import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageMeta from "@/components/base/PageMeta";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import PostCard from "@/components/feature/PostCard";
import { posts } from "@/mocks/posts";
import { topics } from "@/mocks/topics";
import type { Post, Topic } from "@/types/content";
import FilterBar from "./components/FilterBar";

const allPosts = posts as Post[];
const allTopics = topics as Topic[];
const allSchools = Array.from(new Set(allPosts.map((p) => p.school)));

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "CollectionPage",
      name: "讨论区 - 高校吃瓜网",
      description:
        "按板块与学校筛选高校吃瓜讨论帖，覆盖校园热点、宿舍日常、情感八卦、求职实习等主题。",
      url: "https://www.gaoxiaochigua.cn/topics",
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://www.gaoxiaochigua.cn/" },
        {
          "@type": "ListItem",
          position: 2,
          name: "讨论区",
          item: "https://www.gaoxiaochigua.cn/topics",
        },
      ],
    },
  ],
};

export default function Topics() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") ?? "";
  const school = params.get("school") ?? "";
  const sort = params.get("sort") ?? "hot";
  const keyword = params.get("q") ?? "";

  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const list = allPosts.filter((post) => {
      const matchCategory = !category || post.category === category;
      const matchSchool = !school || post.school === school;
      const matchKeyword =
        !kw ||
        post.title.toLowerCase().includes(kw) ||
        post.excerpt.toLowerCase().includes(kw) ||
        post.school.toLowerCase().includes(kw) ||
        post.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchSchool && matchKeyword;
    });

    if (sort === "new") return list.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    if (sort === "discuss") return list.sort((a, b) => b.comments - a.comments);
    return list.sort((a, b) => b.likes - a.likes);
  }, [category, school, sort, keyword]);

  const activeTopic = allTopics.find((t) => t.slug === category);

  const schoolRanking = useMemo(
    () =>
      allSchools
        .map((name) => ({
          name,
          count: allPosts.filter((p) => p.school === name).length,
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 6),
    [],
  );

  return (
    <SiteLayout>
      <PageMeta
        title="讨论区 - 高校吃瓜论坛 | 按学校与板块查看热门帖子"
        description="高校吃瓜网讨论区支持按校园热点、宿舍日常、情感八卦、求职实习等板块以及学校筛选热门帖子，也可以直接搜索关键词，快速定位同校吃瓜现场。"
        keywords="高校吃瓜讨论,高校吃瓜论坛,高校吃瓜社区,校园热点讨论,同校吃瓜"
        path="/topics"
      >
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </PageMeta>

      <div className="mx-auto w-full px-4 pb-16 pt-8 md:px-8 md:pb-20 md:pt-12">
        <Breadcrumbs items={[{ label: "讨论区" }]} />

        <header className="mt-5 max-w-3xl">
          <h1 className="font-heading text-3xl font-black leading-tight tracking-tight text-foreground-950 md:text-[44px]">
            讨论区
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
            {activeTopic
              ? `正在浏览「${activeTopic.name}」板块：${activeTopic.description}`
              : "所有板块的帖子都汇总在这里。选择板块、锁定学校或输入关键词，找到你真正关心的那场讨论。"}
          </p>
        </header>

        <div className="mt-7">
          <FilterBar
            topics={allTopics}
            activeCategory={category}
            onCategory={(value) => update("category", value)}
            schools={allSchools}
            activeSchool={school}
            onSchool={(value) => update("school", value)}
            sort={sort}
            onSort={(value) => update("sort", value)}
            keyword={keyword}
            onKeyword={(value) => update("q", value)}
            resultCount={filtered.length}
          />
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr] lg:gap-10">
          <aside className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-sm font-bold text-foreground-950">全部板块</h2>
              <ul className="mt-3.5 flex flex-col">
                {allTopics.map((topic) => (
                  <li key={topic.slug}>
                    <button
                      type="button"
                      onClick={() => update("category", topic.slug === category ? "" : topic.slug)}
                      className={`flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm transition-colors ${
                        category === topic.slug
                          ? "bg-primary-100 font-medium text-primary-700"
                          : "text-foreground-700 hover:bg-background-200"
                      }`}
                    >
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className={topic.icon}></i>
                      </span>
                      <span className="flex-1">{topic.name}</span>
                      <span className="text-xs text-foreground-500">{topic.postCount}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-sm font-bold text-foreground-950">热门学校</h2>
              <ol className="mt-3.5 flex flex-col gap-2">
                {schoolRanking.map((item, index) => (
                  <li key={item.name}>
                    <button
                      type="button"
                      onClick={() => update("school", item.name === school ? "" : item.name)}
                      className={`flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
                        school === item.name
                          ? "bg-accent-100 font-medium text-accent-900"
                          : "text-foreground-700 hover:bg-background-100"
                      }`}
                    >
                      <span className="w-4 font-heading text-xs font-black text-foreground-400">
                        {index + 1}
                      </span>
                      <span className="flex-1 truncate">{item.name}</span>
                      <span className="text-xs text-foreground-500">{item.count}</span>
                    </button>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg border border-dashed border-background-300 p-5">
              <h2 className="flex items-center gap-1.5 font-heading text-sm font-bold text-foreground-950">
                <span className="flex h-4 w-4 items-center justify-center text-accent-600">
                  <i className="ri-shield-check-line"></i>
                </span>
                发言前请留意
              </h2>
              <p className="mt-2.5 text-xs leading-relaxed text-foreground-600">
                请避免发布可识别个人的隐私信息与未经证实的指控。讨论事件本身没有问题，但请不要让讨论变成对某个人的围攻。
              </p>
            </div>
          </aside>

          <div>
            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {filtered.map((post) => (
                  <PostCard key={post.id} post={post} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-20 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-search-eye-line text-2xl"></i>
                </span>
                <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  没有找到相关讨论
                </h2>
                <p className="mt-2 max-w-sm text-sm text-foreground-600">
                  换个关键词试试，或者清除筛选条件查看全部板块的帖子。
                </p>
                <button
                  type="button"
                  onClick={() => setParams(new URLSearchParams(), { replace: true })}
                  className="mt-5 cursor-pointer rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
                >
                  清除全部筛选
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}